import React from "react";
import styled from "styled-components";

import { brand_orange } from "../constants/styles";
import useDebouncedSearch from "../hooks/useDebouncedSearch";
import SearchStatus from "./SearchStatus";

// A debounced search input that owns its own draft state.
//
// TWO BOUNDARIES MAKE TYPING FREE, and both matter:
//
//   1. State boundary. The typed value deliberately does NOT live in the
//      page-level hook. It used to, so one setState per keystroke re-rendered the
//      whole catalog page — FilterBar, the 30-odd sidebar buttons, all 12 subgrid
//      cards, Header, Footer. Measured: 75ms of blocking work per character, ~4x
//      the 16ms frame budget. Typing visibly stuttered.
//
//   2. The input is UNCONTROLLED (`defaultValue` + ref). The browser owns the
//      value, so React does not re-render or rewrite the DOM on a keystroke at
//      all. React is told only when the visible STATUS changes — three times for
//      a fourteen-character word, not fourteen.
//
// Together: typing costs what typing in a plain HTML input costs.
//
// Two rules for callers, both load-bearing:
//   * `onCommit` must be referentially stable (useCallback), or the debounce
//     timer is rebuilt on every parent render.
//   * `resetToken` should change when the parent clears its filters; the box
//     follows without the parent having to own its value.
//
// `initialValue` is read once, on mount — it seeds an uncontrolled input, so
// later changes to it are ignored by design. Use `resetToken` to re-seed.

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
  width: 100%;
`;

const Input = styled.input`
  width: 100%;
  min-width: 0;
  height: 44px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  background: #fff;
  font-size: 14px;
  font-family: inherit;
  color: #1f2937;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: ${brand_orange};
    box-shadow: 0 0 0 3px rgba(239, 135, 38, 0.15);
  }

  [data-theme="dark"] & {
    background: #0f172a;
    border-color: rgba(255, 255, 255, 0.14);
    color: #f3f4f6;
  }
`;

function SearchBox({
  id,
  initialValue = "",
  onCommit,
  placeholder,
  ariaLabel,
  loading = false,
  resetToken = 0,
}) {
  const search = useDebouncedSearch({ initialValue, onCommit });

  // `resetToken` changing means the parent cleared its filters. Skip the first
  // run so an initial value from the URL is not wiped on mount.
  const seenResetToken = React.useRef(resetToken);
  React.useEffect(() => {
    if (seenResetToken.current === resetToken) return;
    seenResetToken.current = resetToken;
    search.reset("");
  }, [resetToken, search.reset]);

  const statusId = `${id}-status`;

  return (
    <Wrap>
      <Input
        id={id}
        type="search"
        ref={search.inputRef}
        defaultValue={initialValue}
        onChange={search.onInputChange}
        onKeyDown={search.keyDown}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-describedby={statusId}
        autoComplete="off"
      />
      <SearchStatus
        id={statusId}
        phase={search.status.phase}
        charactersNeeded={search.status.charactersNeeded}
        minLength={search.minLength}
        loading={loading}
      />
    </Wrap>
  );
}

// Memoized so a parent re-render (new results landing, `loading` flipping) does
// not re-render the input the user is actively typing into.
export default React.memo(SearchBox);
